import { useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { Input, Pagination } from "antd";
import { TablePaginationConfig } from "antd/es/table";
import { CheckCircleFilled, CloseCircleFilled, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import StringUtils from "../../../util/StringUtils";
import KeyCodeUtils from "../../../util/KeyCodeUtils";
import DeviceApi from "../../../api/DeviceApi";
import DeviceDetails from "./components/device-details/DeviceDetails";
import { DeviceManagementWrapper, ToolBarWrapper, FilterItem, DeviceOverviewWrapper, DeviceCardsContainer, DeviceCardWrapper, CardHeader, Name, Status, InfoLabel, InfoValue, PaginationWrapper, LastLine, ViewDetailButton } from "./DeviceManagement.styled";
import DeviceOverview from "./components/device-overview/DeviceOverview";

interface DeviceManagementProps {

}

interface TableParams {
    pagination?: TablePaginationConfig;
}

const DeviceManagement: React.FC<DeviceManagementProps> = (props)=> {
    // api
    let deviceApi = new DeviceApi();
    let navigate = useNavigate();
    
    // state
    let [deviceViews, setDeviceViews] = useState([]);
    let [search, setSearch] = useState("");

    let [loading, setLoading] = useState(true);
    let [tableParams, setTableParams] = useState<TableParams>({
        pagination: {
            current: 1,
            pageSize: 10, 
            total: 0,
        },
    });

    // events
    let viewDeviceDetails = (device: any)=> {
        navigate(`/device-management/device/${device.device.id}`);
    };

    let onPaginationChange = (page: number, pageSize?: number) => {
        const newPagination = {
            ...tableParams.pagination,
            current: page,
            pageSize: pageSize || tableParams.pagination?.pageSize,
        };
        setTableParams({ pagination: newPagination });
        loadDeviceViews({ pageNo: page, pageSize: newPagination.pageSize });
    };

    let onSearchKeyDown = (e)=> {
        if (e.keyCode == KeyCodeUtils.ENTER) {
            loadDeviceViews();
        }
    };

    // load device views
    let loadDeviceViews = async (opts?: { pageNo?: number, pageSize?: number })=> {
        // loading animation
        setLoading(true);

        const pageNo = opts?.pageNo ?? (tableParams.pagination?.current || 1);
        const pageSize = opts?.pageSize ?? (tableParams.pagination?.pageSize || 10);
        
        let deviceViewsPage = (await deviceApi.queryViews({
            deviceTemplateId: "",
            productModel: "",
            search: search,
            pageNo: pageNo,
            pageSize: pageSize,
        })).data.data;

        let newTableParams = {pagination: {...tableParams.pagination, current: pageNo, pageSize: pageSize}};
        newTableParams.pagination.total = deviceViewsPage.totalElements;
        setTableParams(newTableParams);
        setDeviceViews(deviceViewsPage.elements);
        setLoading(false);
    };

    // effect
    useEffect(()=> {
        loadDeviceViews();
    }, []);

    useEffect(()=> {
        loadDeviceViews();
    }, [search]);
    
    // render
    return (
        <DeviceManagementWrapper>
            <DeviceOverviewWrapper>
                <DeviceOverview />
            </DeviceOverviewWrapper>

            <ToolBarWrapper>
                <FilterItem>
                    <Input 
                        placeholder="请输入关键字" 
                        suffix={<SearchOutlined />} 
                        size="large" 
                        value={search} 
                        onChange={(e)=> setSearch(e.target.value)} 
                        onKeyDown={(e)=> onSearchKeyDown(e)}
                    />
                </FilterItem>
            </ToolBarWrapper>

             <DeviceCardsContainer>
                {loading ? (
                    <DeviceCardWrapper >
                        <div style={{textAlign: 'center', padding: '20px', color: '#8c8c8c'}}>
                            加载中...
                        </div>
                    </DeviceCardWrapper>
                ) : deviceViews.length > 0 ? (
                deviceViews.map(record => (
                    <DeviceCardWrapper key={record.device.id}>
                        <CardHeader>
                            <Name>{StringUtils.emptyToSlash(record.device.name)}</Name>
                            {
                                record.node.status == 'ONLINE'
                                ? <Status><CheckCircleFilled style={{color: 'green', marginRight: '10px'}}/>在线</Status>
                                : <Status><CloseCircleFilled style={{color: 'grey', marginRight: '10px'}} />离线</Status>
                            }                            
                        </CardHeader>
                        <div style={{flex: '1 1 auto'}} ></div>
                        <InfoLabel>最后在线时间:</InfoLabel>
                        <LastLine>
                            <InfoValue>{moment(parseInt(record.node.lastSeenTime)).format("YYYY-MM-DD HH:mm:ss") }</InfoValue>
                            <ViewDetailButton><a onClick={()=> viewDeviceDetails(record)}>查看详情</a></ViewDetailButton>
                        </LastLine>
                    </DeviceCardWrapper>
                ))
                ) : (
                <div style={{gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: '#8c8c8c'}}>
                    暂无设备数据
                </div>
                )}
            </DeviceCardsContainer>
            
             {!loading && deviceViews.length > 0 && (
                <PaginationWrapper>
                    <Pagination
                        simple
                        current={tableParams.pagination?.current}
                        pageSize={tableParams.pagination?.pageSize}
                        total={tableParams.pagination?.total}
                        onChange={onPaginationChange}
                        onShowSizeChange={(current, size) => onPaginationChange(1, size)}
                        showSizeChanger
                        showQuickJumper
                        showTotal={(total, range) => 
                            `第 ${range[0]}-${range[1]} 条/共 ${total} 条`
                        }
                    />
                </PaginationWrapper>
            )}

            <Routes>
                <Route path="device/:deviceId" element={<DeviceDetails />} />
            </Routes>
        </DeviceManagementWrapper>
    );
}

export default DeviceManagement;
